import classNames from 'classnames';
import { useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import DropdownMenu from '@alltrails/denali/components/DropdownMenu';
import RadioGroup from '@alltrails/denali/components/RadioGroup';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import type { DropdownMenuOption } from '@alltrails/denali/types';
import type { JSX } from 'react';
import Sort from '@alltrails/denali/icons/Sort';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import IconButton from '@alltrails/denali/components/IconButton';
import { Modal } from '@alltrails/modal';
import Button from '@alltrails/denali/components/Button';
import type { SortOption } from '../../types/Reviews';
import styles from './ReviewsSort.module.scss';

const messages = defineMessages({
  SORT_BY: { defaultMessage: 'Sort by' },
  AT_SORT: { defaultMessage: 'AllTrails sort' },
  DESC: { defaultMessage: 'Newest' },
  HIGHEST_RATED: { defaultMessage: 'Highest rating' },
  LOWEST_RATED: { defaultMessage: 'Lowest rating' }
});

export type ReviewsSortProps = {
  sortOption: SortOption;
  onSortChanged: (newSort: SortOption) => void;
  sticky?: boolean;
};

const ReviewsSort = ({ sortOption, onSortChanged, sticky }: ReviewsSortProps): JSX.Element => {
  const isMobile = useIsMobileSizedScreen();
  const [isOpen, setIsOpen] = useState(false);

  const {
    formattedDefaultMessages: { SORT_BY, AT_SORT, DESC, HIGHEST_RATED, LOWEST_RATED }
  } = useFormatMessage(messages);

  const optionLabels: Record<Exclude<SortOption, 'asc'>, string> = {
    atSort: AT_SORT,
    desc: DESC,
    highestRated: HIGHEST_RATED,
    lowestRated: LOWEST_RATED
  };

  if (isMobile) {
    const radioOptions = (Object.keys(optionLabels) as Exclude<SortOption, 'asc'>[]).map(key => ({
      testId: `reviewsSort-radio-${key}`,
      text: optionLabels[key],
      value: key,
      selected: sortOption === key
    }));

    return (
      <div className={classNames(styles.labelAndSelect, { [styles.sticky]: sticky })}>
        <IconButton icon={{ Component: Sort }} onClick={() => setIsOpen(true)} size="sm" testId="reviewsSort-mobile" title={SORT_BY} variant="flat" />
        <Modal
          modalClassName={styles.sortBottomModal}
          isOpen={isOpen}
          onCloseRequest={() => setIsOpen(false)}
          size="sm"
          title={<FormattedMessage defaultMessage="Sort reviews" />}
          testId="reviewsSort-mobile-modal"
        >
          <RadioGroup
            name="reviewsSort"
            onChange={value => {
              onSortChanged(value as SortOption);
              setIsOpen(false);
            }}
            options={radioOptions}
          />
        </Modal>
      </div>
    );
  } else {
    const dropdownOptions: DropdownMenuOption[] = (Object.keys(optionLabels) as Exclude<SortOption, 'asc'>[]).map(key => ({
      testId: `reviewsSort-dropdown-${key}`,
      title: optionLabels[key],
      selected: sortOption === key,
      onClick: () => {
        onSortChanged(key);
        setIsOpen(false);
      }
    }));

    return (
      <div className={classNames(styles.labelAndSelect, { [styles.sticky]: sticky })}>
        <DropdownMenu
          anchor={
            <Button
              className={classNames(styles.sortButton, { [styles.dropdownOpen]: isOpen })}
              icon={{ Component: Sort }}
              onClick={() => setIsOpen(!isOpen)}
              size="sm"
              testId="reviewsSort-desktop"
              text={
                <>
                  {optionLabels[sortOption]}
                  <ChevronSm orientation={isOpen ? 'up' : 'down'} size="xs" />
                </>
              }
              title={SORT_BY}
              variant="flat"
            />
          }
          className={styles.dropdown}
          isOpen={isOpen}
          onCloseRequest={() => setIsOpen(false)}
          options={dropdownOptions}
          placement="bottom-end"
          testId="reviewsSort"
        />
      </div>
    );
  }
};

export default ReviewsSort;
